import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowUpRight, Sparkles, Waves, Compass, CircleDot, Hourglass,
  Menu, X, Check, ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_DESKTOP = "https://static.prod-images.emergentagent.com/jobs/64747960-ace3-4934-a535-1ed0557e010d/images/84a9b29402f289d32cbf38b758bbc2f0cd25bb0aa7631e5af7b47fbd3926c14a.png";
const HERO_MOBILE = "https://static.prod-images.emergentagent.com/jobs/64747960-ace3-4934-a535-1ed0557e010d/images/3a2522ad113e2e7a42dc90d9bb4ecc96eb5573ec3f5c734fdc3d3d18cecec986.png";
const GATE_DETAIL = "https://static.prod-images.emergentagent.com/jobs/64747960-ace3-4934-a535-1ed0557e010d/images/3305ddf902c7f76b1ab347ccb00ecfe46d82d5db1c7ee3bfead088fb25e91993.png";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const Overline = ({ children, className = "" }) => (
  <span className={`font-accent text-[0.7rem] uppercase tracking-[0.28em] text-ochre ${className}`}>
    {children}
  </span>
);

const NAV = [
  { label: "The Gap", href: "#concept" },
  { label: "The Work", href: "#curriculum" },
  { label: "For Whom", href: "#audience" },
  { label: "Facilitator", href: "#facilitator" },
  { label: "Enroll", href: "#enroll" },
];

const MODULES = [
  { icon: Compass, title: "Mapping The Gap", text: "Locate the precise threshold where insight stalls before it becomes embodied change. Learn to feel the edge of your own integration." },
  { icon: Waves, title: "From Rigidity To Flow", text: "Somatic sequences that move the nervous system out of bracing patterns and into regulated, fluid availability." },
  { icon: CircleDot, title: "Signal From Noise", text: "Refine interoceptive attention so that the body's true signal becomes legible beneath habitual mental static." },
  { icon: Sparkles, title: "Fascia & Felt Sense", text: "Work with the body's connective architecture — the living network through which sensation, memory and meaning travel." },
  { icon: Hourglass, title: "The Ceremonial Pause", text: "Cultivate deliberate stillness as a practice. The pause is where fragmentation reorganises into coherence." },
  { icon: ArrowUpRight, title: "Carrying It Through", text: "Translate the workshop's state into a portable daily ritual so integration outlives the session." },
];

const AUDIENCE = [
  "Practitioners who understand the theory of regulation but feel the gap between knowing and embodying",
  "Therapists, coaches and bodyworkers seeking a somatic framework for transformation",
  "Movement teachers ready to work with fascia, felt sense and nervous-system intelligence",
  "Anyone living in their head who longs to come home to the body — without spiritual bypass",
];

const FAQ = [
  { q: "Is this a live virtual workshop?", a: "Yes. MOVE THRUS™ is delivered live online in an intimate cohort, with all sessions recorded and available to you afterwards for integration." },
  { q: "Do I need prior somatic experience?", a: "No experience is required — only a willingness to feel. The work meets beginners and seasoned practitioners at their own threshold." },
  { q: "What do I need to participate?", a: "A quiet space where you can move freely, a stable connection, and roughly two square metres of floor. Everything else is guided." },
  { q: "What is 'The Integration Gap'?", a: "It is the space between intellectual insight and lived, embodied change — the threshold this workshop is designed to help you move through." },
  { q: "Is there a refund policy?", a: "Enrolment is fully refundable up to seven days before the workshop begins. We want you here by choice, not obligation." },
];

function EnrollForm({ onDone }) {
  const [form, setForm] = useState({ name: "", email: "", intention: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "landing_enroll" });
      toast.success("You're on the list. We'll email you as soon as dates are announced.");
      setForm({ name: "", email: "", intention: "" });
      onDone && onDone();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.success("You're already on the list — we'll be in touch when dates are announced.");
        setForm({ name: "", email: "", intention: "" });
        onDone && onDone();
      } else {
        toast.error("Something interrupted the signal. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-6" data-testid="enroll-form">
      <div className="flex flex-col gap-1">
        <label className="font-accent text-xs uppercase tracking-[0.2em] text-charcoal/50">Your name</label>
        <input
          data-testid="enroll-name-input"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-transparent border-b border-charcoal/20 py-3 focus:outline-none focus:border-lapis transition-colors font-body text-base"
          placeholder="How shall we greet you"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="font-accent text-xs uppercase tracking-[0.2em] text-charcoal/50">Email</label>
        <input
          data-testid="enroll-email-input"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full bg-transparent border-b border-charcoal/20 py-3 focus:outline-none focus:border-lapis transition-colors font-body text-base"
          placeholder="you@example.com"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="font-accent text-xs uppercase tracking-[0.2em] text-charcoal/50">Your intention <span className="lowercase tracking-normal">(optional)</span></label>
        <textarea
          data-testid="enroll-intention-input"
          value={form.intention}
          onChange={(e) => setForm({ ...form, intention: e.target.value })}
          rows={2}
          className="w-full bg-transparent border-b border-charcoal/20 py-3 focus:outline-none focus:border-lapis transition-colors font-body text-base resize-none"
          placeholder="What are you moving through?"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="enroll-submit-button"
        className="group mt-2 inline-flex items-center justify-center gap-3 bg-lapis text-bone px-8 py-4 font-accent text-sm tracking-wide hover:bg-lapis/90 transition-colors disabled:opacity-60"
      >
        {loading ? "Sending…" : "Claim My Free Spot"}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </button>
    </form>
  );
}

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="bg-bone text-charcoal font-body overflow-x-hidden">
      {/* ---------- NAV ---------- */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-bone/85 backdrop-blur-md border-b border-charcoal/5" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <a href="#top" className="flex flex-col leading-none" data-testid="brand-logo">
            <span className="font-display text-2xl tracking-tight">MOVE THRUS<span className="align-super text-xs">™</span></span>
            <span className="font-accent text-[0.6rem] uppercase tracking-[0.3em] text-ochre mt-0.5">Somatic Equilibrium</span>
          </a>
          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="font-accent text-sm tracking-wide text-charcoal/70 hover:text-lapis transition-colors" data-testid={`nav-${n.href.replace("#", "")}`}>
                {n.label}
              </a>
            ))}
          </nav>
          <button
            onClick={() => setDialogOpen(true)}
            data-testid="nav-enroll-cta"
            className="hidden lg:inline-flex items-center gap-2 bg-charcoal text-bone px-6 py-3 font-accent text-sm tracking-wide hover:bg-lapis transition-colors"
          >
            Join Free <ArrowUpRight className="h-4 w-4" />
          </button>
          <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
        {menuOpen && (
          <div className="lg:hidden bg-bone border-t border-charcoal/10 px-6 py-6 flex flex-col gap-4" data-testid="mobile-menu">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setMenuOpen(false)} className="font-accent text-sm tracking-wide text-charcoal/80">{n.label}</a>
            ))}
          </div>
        )}
      </header>

      {/* ---------- HERO ---------- */}
      <section id="top" className="relative min-h-screen flex items-center pt-20">
        {/* desktop image right */}
        <div className="absolute inset-y-0 right-0 w-full lg:w-[62%] hidden sm:block">
          <img src={HERO_DESKTOP} alt="A horse and rider moving through an ancient lapis threshold at golden dawn" className="h-full w-full object-cover object-right" />
          <div className="absolute inset-0 bg-gradient-to-r from-bone via-bone/40 to-transparent lg:from-bone lg:via-bone/10" />
        </div>
        {/* mobile image */}
        <div className="absolute inset-0 sm:hidden">
          <img src={HERO_MOBILE} alt="Ancient lapis threshold" className="h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-bone via-bone/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <motion.div initial="hidden" animate="show" variants={stagger} className="max-w-xl">
            <motion.div variants={fadeUp} className="flex items-center gap-3 flex-wrap">
              <Overline>A Virtual Somatic Education Workshop</Overline>
              <span data-testid="hero-coming-soon-badge" className="inline-flex items-center bg-lapis text-bone px-3 py-1 font-accent text-[0.65rem] uppercase tracking-[0.2em]">
                Free · Coming Soon
              </span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-6">
              Cross the gap<br />between <span className="italic text-lapis">knowing</span><br />and <span className="italic text-ochre">becoming</span>.
            </motion.h1>
            <motion.p variants={fadeUp} className="font-body text-lg lg:text-xl text-charcoal/70 leading-relaxed mt-8 max-w-md">
              MOVE THRUS™ guides your nervous system across The Integration Gap — where insight finally becomes embodiment, and fragmentation reorganises into flow.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-10">
              <button
                onClick={() => setDialogOpen(true)}
                data-testid="hero-enroll-cta"
                className="group inline-flex items-center gap-3 bg-lapis text-bone px-9 py-4 font-accent text-sm tracking-wide hover:bg-lapis/90 transition-colors"
              >
                Claim My Free Spot <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a href="#concept" data-testid="hero-learn-cta" className="inline-flex items-center gap-2 px-2 py-4 font-accent text-sm tracking-wide text-charcoal/70 hover:text-lapis transition-colors border-b border-transparent hover:border-lapis">
                What is the gap?
              </a>
            </motion.div>
            <motion.div variants={fadeUp} className="flex items-center gap-6 mt-12 text-charcoal/55 font-accent text-xs uppercase tracking-[0.18em]">
              <span>Live Online</span><span className="h-3 w-px bg-charcoal/20" /><span>Free Event</span><span className="h-3 w-px bg-charcoal/20" /><span>Dates Coming Soon</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ---------- CONCEPT ---------- */}
      <section id="concept" className="relative py-28 lg:py-40 bg-surface grain overflow-hidden">
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.div variants={fadeUp}><Overline>The Integration Gap</Overline></motion.div>
            <motion.p variants={fadeUp} className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.25] tracking-tight mt-8 text-charcoal">
              There is a threshold between understanding something and <span className="italic text-lapis">living</span> it. Most of us stand at its edge for years. MOVE THRUS™ is the passage — a ceremonial, nervous-system practice for moving from <span className="italic text-ochre">rigidity into fluidity</span>, from thought into embodiment.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-3 gap-10 mt-20 text-left">
            {[
              { t: "Signal from noise", d: "Hear the body beneath the static." },
              { t: "Embodiment from thought", d: "Let insight settle into tissue." },
              { t: "Integration from fragmentation", d: "Reorganise the scattered into the whole." },
            ].map((x, i) => (
              <motion.div key={i} variants={fadeUp} className="border-t border-charcoal/15 pt-6">
                <span className="font-display text-5xl text-ochre/80">0{i + 1}</span>
                <h3 className="font-display text-2xl mt-3">{x.t}</h3>
                <p className="text-charcoal/60 mt-2 text-sm leading-relaxed">{x.d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---------- CURRICULUM ---------- */}
      <section id="curriculum" className="py-28 lg:py-40 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="max-w-2xl">
          <motion.div variants={fadeUp}><Overline>The Work</Overline></motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-light text-4xl lg:text-5xl tracking-tight mt-5">Six passages through the threshold</motion.h2>
          <motion.p variants={fadeUp} className="text-charcoal/65 text-lg mt-5 leading-relaxed">A sequenced arc of somatic practice — each module a deliberate step from bracing toward coherence.</motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10 mt-16 border border-charcoal/10">
          {MODULES.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div key={i} variants={fadeUp} className="group bg-bone p-9 lg:p-10 flex flex-col gap-5 hover:bg-surface transition-colors duration-500" data-testid={`module-card-${i}`}>
                <div className="flex items-center justify-between">
                  <Icon className="h-6 w-6 text-lapis" strokeWidth={1.4} />
                  <span className="font-display text-3xl text-charcoal/15 group-hover:text-ochre/50 transition-colors">0{i + 1}</span>
                </div>
                <h3 className="font-display text-2xl">{m.title}</h3>
                <p className="text-charcoal/60 text-sm leading-relaxed">{m.text}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* ---------- AUDIENCE ---------- */}
      <section id="audience" className="py-28 lg:py-36 bg-charcoal text-bone relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="lg:col-span-5">
            <motion.div variants={fadeUp}><span className="font-accent text-[0.7rem] uppercase tracking-[0.28em] text-ochre">For Whom</span></motion.div>
            <motion.h2 variants={fadeUp} className="font-display font-light text-4xl lg:text-5xl tracking-tight mt-5 text-bone">This is for those standing at the edge.</motion.h2>
            <motion.p variants={fadeUp} className="text-bone/55 text-lg mt-6 leading-relaxed">If you have collected the knowledge but still feel the distance to the living of it — you are exactly who this was built for.</motion.p>
          </motion.div>
          <motion.ul initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="lg:col-span-7 flex flex-col">
            {AUDIENCE.map((a, i) => (
              <motion.li key={i} variants={fadeUp} className="flex gap-5 py-6 border-t border-bone/10 last:border-b" data-testid={`audience-item-${i}`}>
                <span className="mt-1 h-2.5 w-2.5 rotate-45 bg-ochre shrink-0" />
                <span className="text-bone/80 text-lg leading-relaxed">{a}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* ---------- FACILITATOR ---------- */}
      <section id="facilitator" className="py-28 lg:py-40 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-14 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="lg:col-span-6 relative">
            <img src={GATE_DETAIL} alt="The Ishtar-inspired threshold of Somatic Equilibrium" className="w-full object-cover aspect-[4/5] lg:aspect-square" />
            <div className="absolute -bottom-6 -right-2 lg:right-8 bg-bone border border-charcoal/10 px-8 py-6 max-w-[16rem]">
              <span className="font-display text-2xl">Somatic Equilibrium</span>
              <p className="text-charcoal/55 text-xs mt-1 font-accent uppercase tracking-[0.18em]">Embodiment Studio</p>
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="lg:col-span-6 lg:pl-8">
            <motion.div variants={fadeUp}><Overline>The Facilitator</Overline></motion.div>
            <motion.h2 variants={fadeUp} className="font-display font-light text-4xl lg:text-5xl tracking-tight mt-5">A practice built at the meeting of ancient and futuristic.</motion.h2>
            <motion.p variants={fadeUp} className="text-charcoal/65 text-lg mt-6 leading-relaxed">
              Somatic Equilibrium creates ceremonial, intelligent environments for nervous-system regulation. Our work draws on fascia science, interoception and the felt sense — held within a framework as deliberate as architecture itself.
            </motion.p>
            <motion.p variants={fadeUp} className="text-charcoal/65 text-lg mt-4 leading-relaxed">
              MOVE THRUS™ is our signature passage: grounded, premium, and mythic but believable. No fantasy. Only the quiet, repeatable work of coming home to the body.
            </motion.p>
            <motion.div variants={fadeUp} className="flex gap-12 mt-10">
              <div><div className="font-display text-4xl text-lapis">12+</div><div className="text-charcoal/55 text-sm mt-1">Years in somatic practice</div></div>
              <div><div className="font-display text-4xl text-lapis">2k+</div><div className="text-charcoal/55 text-sm mt-1">Practitioners guided</div></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ---------- PRICING / DETAILS ---------- */}
      <section id="enroll" className="py-28 lg:py-36 bg-surface grain relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-stretch">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="lg:col-span-5 flex flex-col justify-center">
            <motion.div variants={fadeUp}><Overline>Enrolment</Overline></motion.div>
            <motion.h2 variants={fadeUp} className="font-display font-light text-4xl lg:text-5xl tracking-tight mt-5">Step through the gate.</motion.h2>
            <motion.p variants={fadeUp} className="text-charcoal/65 text-lg mt-6 leading-relaxed">An intimate cohort. Live online. Held with care — and completely free. Dates are coming soon; claim your place and be the first to know.</motion.p>
            <motion.div variants={fadeUp} className="mt-10 flex flex-col gap-3 font-accent text-sm">
              {["Three live somatic sessions","Lifetime access to recordings","Guided integration workbook","Private cohort circle"].map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-charcoal/75"><Check className="h-4 w-4 text-lapis" /> {f}</div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="lg:col-span-7 bg-charcoal text-bone p-10 lg:p-14 border-t-4 border-t-ochre">
            <div className="flex items-baseline justify-between flex-wrap gap-4 border-b border-bone/15 pb-8">
              <div>
                <span className="font-accent text-[0.7rem] uppercase tracking-[0.28em] text-ochre">The Workshop</span>
                <div className="font-display text-3xl mt-2">MOVE THRUS™ Live Cohort</div>
              </div>
              <div className="text-right">
                <div className="font-display text-5xl text-ochre" data-testid="pricing-free-label">Free</div>
                <div className="text-bone/50 text-xs font-accent uppercase tracking-[0.18em] mt-1" data-testid="pricing-coming-soon">Dates · Coming Soon</div>
              </div>
            </div>
            <div className="mt-8">
              <EnrollForm onDone={() => {}} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="py-28 lg:py-36 max-w-3xl mx-auto px-6 lg:px-12">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
          <Overline>Questions</Overline>
          <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight mt-5">Before you cross</h2>
        </motion.div>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-charcoal/15">
              <AccordionTrigger className="font-display text-xl lg:text-2xl py-6 hover:no-underline text-left" data-testid={`faq-trigger-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-charcoal/65 text-base leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer className="bg-charcoal text-bone">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="flex flex-col lg:flex-row justify-between gap-12 border-b border-bone/10 pb-14">
            <div className="max-w-sm">
              <span className="font-display text-3xl">MOVE THRUS™</span>
              <p className="text-bone/50 mt-4 leading-relaxed">A virtual somatic education workshop by Somatic Equilibrium. From rigidity into fluidity.</p>
              <button onClick={() => setDialogOpen(true)} data-testid="footer-enroll-cta" className="mt-8 inline-flex items-center gap-2 border border-bone/30 px-7 py-3 font-accent text-sm tracking-wide hover:bg-bone hover:text-charcoal transition-colors">
                Claim My Free Spot <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>
            <div className="flex gap-16">
              <div className="flex flex-col gap-3">
                <span className="font-accent text-[0.65rem] uppercase tracking-[0.25em] text-bone/40">Navigate</span>
                {NAV.map((n) => <a key={n.href} href={n.href} className="text-bone/70 hover:text-ochre transition-colors text-sm">{n.label}</a>)}
              </div>
              <div className="flex flex-col gap-3">
                <span className="font-accent text-[0.65rem] uppercase tracking-[0.25em] text-bone/40">Studio</span>
                <span className="text-bone/70 text-sm">Live · Online</span>
                <a href="mailto:pschlachter@seededsomatics.com" className="text-bone/70 hover:text-ochre transition-colors text-sm" data-testid="footer-contact-email">pschlachter@seededsomatics.com</a>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between gap-4 pt-8 text-bone/40 text-xs font-accent uppercase tracking-[0.18em]">
            <span>© {new Date().getFullYear()} Somatic Equilibrium</span>
            <span>Nervous-system regulation, not fantasy.</span>
          </div>
        </div>
      </footer>

      {/* ---------- ENROLL DIALOG ---------- */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-bone border-charcoal/10 max-w-md" data-testid="enroll-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-light">Claim your free spot</DialogTitle>
            <DialogDescription className="text-charcoal/60">
              MOVE THRUS™ is free. Dates are coming soon — join the list and we'll email you the moment they're announced.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <EnrollForm onDone={() => setDialogOpen(false)} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
